import { FC, useCallback, useEffect, useRef, useState } from 'react';
import GroupOperationDialog, { IGroupOperationItem } from '@srm/ui/group-operation-dialog';
import { sendNotification } from '@srm/ui/lib/notifications';
import { errorTextWithTraceId } from '@srm/core/errors';
import { IBaseAction } from '@models/actions/IBaseAction';
import { IGroupActionItemResult, IGroupActionPlan } from '@models/actions/IGroupActionPlan';
import { IGroupActionPlanSource } from '@services/actions/IActionService';
import { beginGroupRun } from '@services/actions/groupOperationRun';
import {
    failedIds,
    isTooManyForGroupOperation,
    planKeyMap,
    previewItems,
    resultItems,
    resultToast,
    runnableIds,
    shouldSkipPreview,
    tooManyItemsText,
} from './groupOperationFlowModel';

export interface IGroupOperationFlowProps {
    action: IBaseAction;
    /** Ключи выбранных строк списка на момент запуска */
    rowKeys: string[];
    source: IGroupActionPlanSource;
    /**
     * Запуск исполнителя действия для части строк: его форма (комментарий, подпись) показывается как обычно.
     * `onComplete(true)` — исполнитель выполнил запрос, `false` — отмена или отказ.
     */
    runExecutor: (rowKeys: string[], onComplete: (isSucceed: boolean) => void) => void;
    /** Данные списка изменились — перечитать */
    onModified?: () => void;
    /** Окно закрыто, групповая операция завершена */
    onFinished: () => void;
    /** Выделить строки в списке */
    onSelectKeys?: (rowKeys: string[]) => void;
}

type FlowPhase = 'loading' | 'preview' | 'form' | 'result';

/** Окно загрузки плана показывается не сразу: быстрый план без предпросмотра не мелькает окном. */
const LOADING_DELAY_MS = 250;

/**
 * Групповая операция по объектам (`groupMode="perItem"`): план «выполнится / нельзя», затем форма действия
 * исполнителя для проходящих, затем построчный итог в том же окне.
 */
const GroupOperationFlow: FC<IGroupOperationFlowProps> = ({
    action,
    rowKeys,
    source,
    runExecutor,
    onModified,
    onFinished,
    onSelectKeys,
}) => {
    const [phase, setPhase] = useState<FlowPhase>('loading');
    const [plan, setPlan] = useState<IGroupActionPlan>();
    const [loadError, setLoadError] = useState<string>();
    const [loadingShown, setLoadingShown] = useState(false);
    const [items, setItems] = useState<IGroupOperationItem[]>([]);
    const keysById = useRef(planKeyMap(rowKeys, source.toPlanId));
    /** Сервер такой выбор не примет ни в плане, ни в выполнении — без запроса и без «Повторить» */
    const tooMany = isTooManyForGroupOperation(keysById.current.size);
    const results = useRef(new Map<string, IGroupActionItemResult>());
    const runIds = useRef(new Set<string>());
    /** Завершение активного запуска (`beginGroupRun`): пока он есть, повторный запуск не начинается. */
    const endRun = useRef<() => void>(undefined);
    const planRequest = useRef<AbortController>(undefined);
    const actionName = action.baseDisplayName || action.displayName;

    const rowKeysOf = useCallback((ids: string[]) => ids.flatMap((id) => keysById.current.get(id) ?? [id]), []);

    const run = useCallback(
        (currentPlan: IGroupActionPlan, ids: string[], quiet: boolean) => {
            if (endRun.current) return;
            let delivered: IGroupActionItemResult[] | undefined;
            const end = beginGroupRun(
                action.key,
                (itemResults) => {
                    delivered = itemResults;
                },
                source.groupRunParams(),
            );
            endRun.current = end;
            setPhase('form');
            runExecutor(rowKeysOf(ids), (isSucceed) => {
                end();
                endRun.current = undefined;
                if (!isSucceed && !delivered) {
                    // Форма отменена или запрос отклонён целиком (отказ показан исполнителем)
                    if (quiet || runIds.current.size === 0) onFinished();
                    else setPhase('result');
                    return;
                }

                ids.forEach((id) => runIds.current.add(id));
                if (delivered) delivered.forEach((result) => results.current.set(result.id, result));
                else ids.forEach((id) => results.current.set(id, { id, success: true }));

                const next = resultItems(currentPlan, [...runIds.current], [...results.current.values()]);
                setItems(next);
                onModified?.();
                sendNotification(resultToast(actionName, next));

                if (quiet && failedIds(next).length === 0) onFinished();
                else setPhase('result');
            });
        },
        [action.key, actionName, onFinished, onModified, rowKeysOf, runExecutor, source],
    );

    useEffect(() => {
        if (phase !== 'loading') return;
        const timer = window.setTimeout(() => setLoadingShown(true), LOADING_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [phase]);

    const loadPlan = () => {
        planRequest.current?.abort();
        if (tooMany) {
            setLoadError(tooManyItemsText(source.objectNounForms));
            setPhase('preview');
            return;
        }
        const controller = new AbortController();
        planRequest.current = controller;
        setLoadError(undefined);
        setPhase('loading');
        source
            .planGroupAction([...keysById.current.keys()], action.key, controller.signal)
            .then(({ data }) => {
                if (controller.signal.aborted) return;
                setPlan(data);
                if (shouldSkipPreview(data)) {
                    run(data, runnableIds(data), true);
                } else {
                    setItems(previewItems(data));
                    setPhase('preview');
                }
            })
            .catch((error: unknown) => {
                if (controller.signal.aborted) return;
                setLoadError(errorTextWithTraceId(error));
                setPhase('preview');
            });
    };

    useEffect(() => {
        // План строится один раз на запуск операции; повтор после отказа — «Повторить» окна
        loadPlan();
        return () => {
            planRequest.current?.abort();
            // Окно ушло вместе со списком посреди формы действия: запросы этого действия снова прежние
            endRun.current?.();
            endRun.current = undefined;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const failed = failedIds(items);

    return (
        <GroupOperationDialog
            opened={phase === 'loading' ? loadingShown : phase !== 'form'}
            actionName={actionName}
            nounForms={source.objectNounForms}
            phase={phase === 'result' ? 'result' : phase === 'loading' ? 'loading' : 'preview'}
            items={items}
            selectedCount={keysById.current.size}
            groupErrors={phase === 'result' ? undefined : plan?.groupErrors}
            loadError={loadError}
            onConfirm={plan ? () => run(plan, runnableIds(plan), false) : undefined}
            onRetryLoad={
                tooMany
                    ? undefined
                    : () => {
                          setLoadingShown(true);
                          loadPlan();
                      }
            }
            onClose={onFinished}
            onRetryFailed={plan && failed.length > 0 ? () => run(plan, failed, false) : undefined}
            onSelectFailed={
                onSelectKeys && failed.length > 0
                    ? () => {
                          onSelectKeys(rowKeysOf(failed));
                          onFinished();
                      }
                    : undefined
            }
        />
    );
};

export default GroupOperationFlow;
